// src/providers/firebase.ts
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import 'firebase/compat/functions';
import { getApp } from 'firebase/app';
import { connectDatabaseEmulator, getDatabase } from 'firebase/database';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import {
    getMessaging,
    isSupported as isMessagingSupported,
    type Messaging,
} from 'firebase/messaging';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
};

// Initialize Firebase (only once)
if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
}

const firebaseApp = firebase.app();
const rtdb = getDatabase(getApp());
const functions = getFunctions(getApp(), 'europe-west6');
const db = getFirestore(getApp());

let messaging: Messaging | null = null;
// isSupported() is async (it probes IndexedDB), so `messaging` is null until it
// resolves. Consumers that need it at mount time (e.g. the foreground push
// listener) must await `messagingReady` rather than reading getMessagingOrNull()
// synchronously, otherwise they can race the init and never attach.
const messagingReady: Promise<Messaging | null> = isMessagingSupported().then(
    supported => {
        if (supported) messaging = getMessaging(getApp());
        return messaging;
    }
);

function getMessagingOrNull(): Messaging | null {
    return messaging;
}

if (import.meta.env.DEV) {
    firebase.firestore.setLogLevel('debug');
}

// Opt-in local emulator wiring: `npm run dev:emulators` (mode "emulators"
// loads .env.emulators, which sets VITE_FIREBASE_EMULATORS=1 and a demo
// project id). Host overrides let dev machines with occupied default ports
// point elsewhere, e.g. VITE_EMULATOR_FIRESTORE=localhost:8091.
// Normal dev/prod builds never enter this branch.
if (import.meta.env.VITE_FIREBASE_EMULATORS === '1') {
    const hostPort = (
        v: string | undefined,
        fallback: string
    ): [string, number] => {
        const [h, p] = (v ?? fallback).split(':');
        return [h, Number(p)];
    };
    const [fsHost, fsPort] = hostPort(
        import.meta.env.VITE_EMULATOR_FIRESTORE,
        'localhost:8080'
    );
    const [authHost, authPort] = hostPort(
        import.meta.env.VITE_EMULATOR_AUTH,
        'localhost:9099'
    );
    const [fnHost, fnPort] = hostPort(
        import.meta.env.VITE_EMULATOR_FUNCTIONS,
        'localhost:5001'
    );
    const [dbHost, dbPort] = hostPort(
        import.meta.env.VITE_EMULATOR_DATABASE,
        'localhost:9001'
    );
    firebase.auth().useEmulator(`http://${authHost}:${authPort}`);
    connectFirestoreEmulator(db, fsHost, fsPort);
    connectFunctionsEmulator(functions, fnHost, fnPort);
    connectDatabaseEmulator(rtdb, dbHost, dbPort);
}

// Export Firebase modules for easy imports
export {
    firebase,
    firebaseApp,
    rtdb,
    functions,
    db,
    getMessagingOrNull,
    messagingReady,
};
export { firebaseConfig };
